import { MicrophoneIcon } from '@heroicons/react/solid';
import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import { ArrowGlyph, tone } from '@/components/agents/primitives';

import { useAnalytics } from '@/contexts/Analytics';
import { useUser } from '@/utils/useUser';

import { useLandingT, useStartHref } from './kit';

// Bar heights of the live waveform, fixed so server and client agree.
const BARS = [
  30, 55, 42, 78, 60, 88, 46, 70, 36, 62, 84, 50, 40, 72, 58, 90, 44, 66, 32,
  58, 80, 48, 70, 40,
];

/**
 * The AI Note Taker page's call to action, in place of the upload box: a
 * recorder that opens the note taker (signing up first when needed).
 */
export default function LandingRecorder({ page }: { page: string }) {
  const { t } = useLandingT();
  const analytics = useAnalytics();
  const { user } = useUser();
  const startHref = useStartHref('record');

  return (
    <div className='rounded-[26px] bg-white p-2 shadow-[0_24px_60px_-30px_rgba(22,12,90,0.45)] ring-1 ring-[#0f0f28]/[0.07] dark:bg-[#14141f] dark:ring-white/[0.08]'>
      <div className='rounded-[20px] bg-[#fafafd] px-6 py-8 text-center dark:bg-white/[0.03]'>
        <div
          aria-hidden='true'
          className='mx-auto flex w-fit items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold tabular-nums shadow-[0_1px_2px_rgba(15,15,40,0.06)] ring-1 ring-[#0f0f28]/[0.06] dark:bg-white/[0.06] dark:ring-white/10'
        >
          <span className='h-2 w-2 rounded-full bg-rose-500' />
          <span className={tone.ink}>12:48</span>
        </div>
        <div
          aria-hidden='true'
          className={clsxm(
            styles.wave,
            styles.waveCenter,
            'mx-auto mt-6 flex h-12 max-w-[16rem] items-center justify-center gap-[4px]',
          )}
        >
          {BARS.map((height, index) => (
            <span
              key={index}
              className='w-[4px] flex-none rounded-full bg-primary-500/80'
              style={
                {
                  height: `${height}%`,
                  '--d': `${(index % 8) * 90}ms`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
        <Link
          href={startHref}
          onClick={() =>
            analytics.track('Transcription Landing Start', {
              page,
              kind: 'record',
              signed_in: Boolean(user),
            })
          }
          className='group mx-auto mt-7 inline-flex items-center gap-3 rounded-full bg-primary-600 py-3 pe-6 ps-3 text-[15px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_10px_30px_-10px_rgba(79,70,229,0.7)] transition-colors duration-200 hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
        >
          <span className='flex h-9 w-9 items-center justify-center rounded-full bg-white/15'>
            <MicrophoneIcon className='h-5 w-5' aria-hidden />
          </span>
          {t('Start recording')}
        </Link>
        <p className={clsxm('mt-4 text-sm', tone.body)}>
          {t('Works in your browser on laptops and phones.')}
        </p>
      </div>
      <Link
        href='/ai-transcription'
        className={clsxm(
          'group mt-2 flex items-center justify-center gap-1.5 rounded-[20px] bg-[#f4f4f8] px-4 py-3 text-sm font-semibold transition-colors hover:text-primary-700 dark:bg-white/[0.05] dark:hover:text-primary-300',
          tone.ink,
        )}
      >
        {t('Already have a recording? Upload it')}
        <ArrowGlyph className='h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' />
      </Link>
    </div>
  );
}
